/**
 * 活动统计（Alice 页面 campaignStats 的复刻）：按活动批次看实收、常规赠送、活动加赠，以及每日与订单明细。
 *
 * 背景：Alice 靠页面里的全局变量记住「从发布历史点了哪一行的统计」；本仓每页独立，批次通过地址参数
 *   ?key=<批次> 传入（发布历史的「统计」按钮跳转而来），没有参数时取当前生效的活动，再没有就取最近发布的批次。
 * 设计意图：版式与 Alice 一致（六张指标卡、说明条、每日效果与订单明细两张表）；金额是美元，赠送是花瓣，
 *   面值按 1 美元 = 1,000 花瓣折算。Alice 没有的「活动批次」下拉是为了从侧栏直接进来时也能切换批次。
 * 关键约束：统计只读真实订单（退款订单已由后端剔除），没有任何本地估算；加载失败把原因写在副标题里。
 */
import { useState } from 'react'
import { campaignApi } from '@ap/api-client/admin-campaign'
import { AG_campaign as G, errorTextC } from '@ap/i18n/admin-campaign'
import { intlTag, useI18n } from '@ap/i18n'
import { fmtDateTime, useLoad, useRoute } from '@ap/ui'
import { OpsPage } from '../../alice/kit.tsx'
import { ToolSelect, fmtNum, fmtUsdStr } from '../../alice/kit-campaign.tsx'

export function CampaignStats() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const route = useRoute()
  const [picked, setPicked] = useState<string | null>(null)
  const overview = useLoad(() => campaignApi.campaign(), [])
  const history = overview.data?.history ?? []
  // 发布历史按版本倒序，同一批次取第一次出现的（最新）版本当名称
  const batches: { key: string; name: string }[] = []
  for (const h of history) {
    if (h.campaign_key && !batches.some((b) => b.key === h.campaign_key)) batches.push({ key: h.campaign_key, name: h.campaign_name || h.title || h.campaign_key })
  }
  const eff = overview.data?.effective_campaign
  const key = picked ?? route.query.get('key') ?? (eff && eff.mode !== 'none' ? eff.campaign_key : null) ?? batches[0]?.key ?? null
  const stats = useLoad(() => (key ? campaignApi.campaignStats(key) : Promise.resolve(null)), [key])
  const s = stats.data?.summary
  const name = stats.data?.campaign_name || batches.find((b) => b.key === key)?.name || key || ''
  const money = (v: string | number | undefined) => fmtUsdStr(v, tag)
  const num = (v: number | undefined) => fmtNum(v, tag)

  const subtitle = !key
    ? t(G.stats.subtitle)
    : stats.error
      ? t(G.stats.subtitleFailed, { msg: errorTextC(stats.error, locale) })
      : stats.loading || !stats.data
        ? t(G.stats.subtitleLoading, { key })
        : t(G.stats.subtitleKey, { key })

  return (
    <OpsPage eyebrow={t(G.stats.eyebrow)} title={t(G.stats.title)} desc={t(G.stats.desc)} chip={t(G.stats.chip)}>
      {!key && overview.data ? (
        <div className="ops-note">{t(G.stats.noCampaign)}</div>
      ) : (
        <section className="ops-section-card campaign-stats-card">
          <div className="ops-section-head">
            <div>
              <div className="ops-section-title">{key ? t(G.stats.sectionTitleFor, { name }) : t(G.stats.sectionTitle)}</div>
              <div className="ops-section-copy">{subtitle}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {batches.length > 1 && (
                <ToolSelect
                  aria-label={t(G.stats.pick)}
                  value={key ?? ''}
                  onChange={(e) => setPicked(e.target.value)}
                  options={batches.map((b) => ({ value: b.key, label: `${b.name} (${b.key})` }))}
                  style={{ fontSize: 12, padding: '6px 28px 6px 10px', maxWidth: 260 }}
                />
              )}
              <button className="btn btn-secondary btn-sm" type="button" onClick={stats.reload}>
                {t(G.stats.refresh)}
              </button>
            </div>
          </div>

          <div className="campaign-stats-grid">
            <div className="campaign-stat">
              <span>{t(G.stats.netPaid)}</span>
              <strong>{s ? money(s.net_paid_usd) : money(0)}</strong>
              <small>{s ? t(G.stats.grossNote, { gross: money(s.gross_paid_usd), refund: money(s.refunded_usd) }) : t(G.stats.refundedNote)}</small>
            </div>
            <div className="campaign-stat">
              <span>{t(G.stats.users)}</span>
              <strong>{num(s?.paid_users ?? 0)}</strong>
              <small>{t(G.stats.ordersNote, { n: num(s?.paid_orders ?? 0), m: num(s?.refunded_orders ?? 0) })}</small>
            </div>
            <div className="campaign-stat">
              <span>{t(G.stats.regular)}</span>
              <strong>{num(s?.regular_bonus_petals ?? 0)}</strong>
              <small>{t(G.stats.regularNote)}</small>
            </div>
            <div className="campaign-stat activity">
              <span>{t(G.stats.campaign)}</span>
              <strong>{num(s?.campaign_bonus_petals ?? 0)}</strong>
              <small>{t(G.stats.campaignRate, { pct: Number(s?.campaign_bonus_rate ?? 0).toFixed(2) })}</small>
            </div>
            <div className="campaign-stat activity">
              <span>{t(G.stats.value)}</span>
              <strong>{money(s?.campaign_value_usd ?? 0)}</strong>
              <small>{t(G.stats.valueNote)}</small>
            </div>
            <div className="campaign-stat">
              <span>{t(G.stats.total)}</span>
              <strong>{num(s?.total_petals ?? 0)}</strong>
              <small>{t(G.stats.totalNote)}</small>
            </div>
          </div>
          <div className="campaign-cost-note">
            {s
              ? t(G.stats.costNoteFor, {
                  n: num(s.campaign_bonus_petals),
                  v: Number(s.campaign_value_usd).toLocaleString(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  r: num(s.refunded_campaign_petals),
                })
              : t(G.stats.costNote)}
          </div>

          <div className="campaign-stats-tables">
            <div className="campaign-stats-table">
              <div className="campaign-stats-table-title">{t(G.stats.daily)}</div>
              <div className="campaign-stats-scroll table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t(G.stats.dailyCols.date)}</th>
                      <th>{t(G.stats.dailyCols.paid)}</th>
                      <th>{t(G.stats.dailyCols.usersOrders)}</th>
                      <th>{t(G.stats.dailyCols.regular)}</th>
                      <th>{t(G.stats.dailyCols.campaign)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.data?.daily ?? []).map((d) => (
                      <tr key={d.date}>
                        <td style={{ whiteSpace: 'nowrap' }}>{d.date}</td>
                        <td>{money(d.paid_usd)}</td>
                        <td>
                          {num(d.users)} / {num(d.orders)}
                        </td>
                        <td>{num(d.regular_bonus_petals)}</td>
                        <td style={{ color: '#7c3aed', fontWeight: 600 }}>{num(d.campaign_bonus_petals)}</td>
                      </tr>
                    ))}
                    {stats.data && stats.data.daily.length === 0 && (
                      <tr>
                        <td colSpan={5} className="empty">
                          {t(G.stats.dailyEmpty)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="campaign-stats-table">
              <div className="campaign-stats-table-title">{t(G.stats.orders)}</div>
              <div className="campaign-stats-scroll table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t(G.stats.orderCols.time)}</th>
                      <th>{t(G.stats.orderCols.user)}</th>
                      <th>{t(G.stats.orderCols.amount)}</th>
                      <th>{t(G.stats.orderCols.regular)}</th>
                      <th>{t(G.stats.orderCols.campaign)}</th>
                      <th>{t(G.stats.orderCols.total)}</th>
                      <th>{t(G.stats.orderCols.status)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.data?.orders ?? []).map((o) => (
                      <tr key={o.order_id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{o.paid_at ? fmtDateTime(o.paid_at, locale) : t(G.common.dash)}</td>
                        <td title={o.account_email || o.account_id}>{o.account_name}</td>
                        <td>{money(o.amount_usd)}</td>
                        <td>{num(o.regular_bonus_petals)}</td>
                        <td style={{ color: '#7c3aed', fontWeight: 600 }}>{num(o.campaign_bonus_petals)}</td>
                        <td>{num(o.total_petals)}</td>
                        <td>{t(G.stats.refund[o.refund_status])}</td>
                      </tr>
                    ))}
                    {stats.data && stats.data.orders.length === 0 && (
                      <tr>
                        <td colSpan={7} className="empty">
                          {t(G.stats.ordersEmpty)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}
    </OpsPage>
  )
}
